import React, { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { getWebsiteSetting } from "../store/slices/userActions";
import "./styles.css";
import "./ver2.css";
import { useAuth } from "../contexts/JWTContext";
import lottoApi from "./api";
import { captureAttribution, money2, pushToast, useLottoSocket } from "./lib";
import { Toasts } from "./ui";
import TurnBar from "./TurnBar";
import DiamondBox from "./DiamondBox";
import { LandingProvider, RESULTS_PAGE_ENABLED, useLanding } from "./landingTheme";

import Logo from "../components/Logo";

import HomePage from "./pages/Home";
import PlayPage from "./pages/Play";
import MyBetsPage from "./pages/MyBets";
import ResultsPage from "./pages/Results";
import YeekeePage from "./pages/Yeekee";
import AccountPage from "./pages/Account";
import AuthPage from "./pages/Auth";
import DepositPage from "./pages/Deposit";
import WithdrawPage from "./pages/Withdraw";
import PromotionPage from "./pages/Promotion";
import CheckinPage from "./pages/Checkin";
import WheelSpinPage from "./pages/WheelSpin";
import ReferFriendPage from "./pages/ReferFriend";
import CreditCodePage from "./pages/CreditCode";
import RankingPage from "./pages/Ranking";
import CashbackPage from "./pages/Cashback";
import MarketingActivityPage from "./pages/MarketingActivity";
import ReviewsPage from "./pages/Reviews";

// เก็บ attribution จาก ?ref/?rf/?utm_* ตั้งแต่โหลดหน้าแรก
captureAttribution();

// หน้าที่ใครก็ดูได้ — ผู้เยี่ยมชมจะเห็นแถบชวนเข้าสู่ระบบท้ายหน้า
const PUBLIC_CTA_PATH = /^\/(results|yeekee|play)(\/|$)/;

// key = คีย์ใน landing config (nav.<key>) ที่แอดมินใช้ซ่อน/เปลี่ยนชื่อเมนู
const NAV = [
  { key: "home", to: "/", label: "หน้าแรก", icon: "solar:home-2-bold-duotone", end: true },
  { key: "results", to: "/results", label: "ผลหวย", icon: "solar:cup-star-bold-duotone" },
  { key: "refer", to: "/refer", label: "แนะนำเพื่อน", icon: "solar:users-group-rounded-bold-duotone" },
  { key: "reviews", to: "/reviews", label: "รีวิวจากลูกค้า", short: "รีวิว", icon: "solar:chat-round-like-bold-duotone" },
  { key: "mybets", to: "/mybets", label: "โพยของฉัน", icon: "solar:clipboard-list-bold-duotone" },
  { key: "account", to: "/account", label: "บัญชี", icon: "solar:user-circle-bold-duotone" },
];

// เมนูที่ผูกกับสวิตช์ใน "ฟีเจอร์หน้าลูกค้า" (/lotto/site-features) — แอดมินปิดสวิตช์แล้วเมนูหายและเข้าหน้านั้นตรงๆ ไม่ได้
// key = คีย์เมนู · ค่า = คีย์ใน GET /lotto/features (API ล่ม/ยังโหลดไม่เสร็จ = แสดงทุกเมนู)
const NAV_FEATURE = { refer: "refer", reviews: "reviews" };

let featuresCache = { at: 0, promise: null };
function loadFeatures() {
  if (!featuresCache.promise || Date.now() - featuresCache.at > 30000) {
    featuresCache = { at: Date.now(), promise: lottoApi.features().catch(() => null) };
  }
  return featuresCache.promise;
}
function useFeatureFlags() {
  const [flags, setFlags] = useState(null);
  useEffect(() => {
    let on = true;
    loadFeatures().then((f) => on && setFlags(f));
    return () => {
      on = false;
    };
  }, []);
  return flags;
}
const featureOn = (flags, key) => !flags || flags[key] !== false;

// เมนูหลังแอดมินปรับ (ซ่อน/เปลี่ยนชื่อ) + สวิตช์ฟีเจอร์ — หน้าแรก/บัญชี ซ่อนไม่ได้
function useNavItems() {
  const { cfg } = useLanding();
  const flags = useFeatureFlags();
  return NAV.filter((n) => cfg.nav?.[n.key]?.visible !== false && (n.key !== "results" || RESULTS_PAGE_ENABLED) && (!NAV_FEATURE[n.key] || featureOn(flags, NAV_FEATURE[n.key]))).map((n) => ({
    ...n,
    label: cfg.nav?.[n.key]?.label || n.label,
    // ชื่อสั้นของแถบล่าง ใช้เฉพาะตอนแอดมินไม่ได้ตั้งชื่อเมนูเอง
    short: cfg.nav?.[n.key]?.label && cfg.nav[n.key].label !== n.label ? undefined : n.short,
  }));
}

// หน้าที่ผูกกับสวิตช์ฟีเจอร์: ปิดอยู่ = แจ้งปิดให้บริการ (กันเข้าตรงผ่านลิงก์)
function FeatureGate({ flag, title, children }) {
  const nav = useNavigate();
  const flags = useFeatureFlags();
  if (featureOn(flags, flag)) return children;
  return (
    <div className="lx-card" style={{ maxWidth: 520, margin: "40px auto", textAlign: "center", padding: 28 }}>
      <Icon icon="solar:lock-keyhole-minimalistic-bold-duotone" width={44} height={44} />
      <h2 className="lx-h2" style={{ marginTop: 10 }}>
        {title}ปิดให้บริการชั่วคราว
      </h2>
      <p style={{ color: "var(--lx-text-dim)" }}>ขออภัยในความไม่สะดวก — กลับมาใช้งานได้เร็วๆ นี้</p>
      <button type="button" className="lx-btn primary" onClick={() => nav("/")}>
        กลับหน้าแรก
      </button>
    </div>
  );
}

// ปุ่มสลับสว่าง/มืด — โชว์เมื่อแอดมินเปิดให้ลูกค้าสลับเองได้
function ThemeToggle() {
  const { mode, canToggle, toggleMode } = useLanding();
  if (!canToggle) return null;
  const dark = mode === "dark";
  return (
    <button
      type="button"
      className="lx-theme-toggle"
      onClick={toggleMode}
      aria-label={dark ? "สลับเป็นโหมดสว่าง" : "สลับเป็นโหมดมืด"}
      title={dark ? "โหมดสว่าง" : "โหมดมืด"}
    >
      <Icon icon={dark ? "solar:sun-2-bold-duotone" : "solar:moon-stars-bold-duotone"} width={19} height={19} />
    </button>
  );
}

function TopBar({ balance, onRefresh }) {
  const { isAuthenticated } = useAuth();
  const nav = useNavigate();
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    setFlash(true);
    const id = setTimeout(() => setFlash(false), 900);
    return () => clearTimeout(id);
  }, [balance]);

  return (
    <div className="lx-topbar">
      <div className="lx-topbar-in">
        <a
          className="lx-brand"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            nav("/");
          }}
        >
          <Logo size="small" />
        </a>
        <div className="lx-balance">
          <ThemeToggle />
          {isAuthenticated ? (
            <>
              <button
                className={`lx-chip ${flash ? "flash" : ""}`}
                onClick={() => {
                  onRefresh();
                  nav("/account");
                }}
                title="ดูกระเป๋าเงิน"
              >
                <span className="lx-coin" />
                <span className="lx-mono">{money2(balance)}</span>
              </button>
              <button className="lx-btn mint sm" onClick={() => nav("/account")}>
                ฝาก-ถอน
              </button>
            </>
          ) : (
            <button className="lx-btn primary sm" onClick={() => nav("/auth")}>
              เข้าสู่ระบบ
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// แถบวิ่งประกาศ — โชว์เฉพาะเมื่อแอดมินตั้งข้อความไว้ (webSetting.marqueeText)
function MarqueeBar({ text }) {
  if (!text) return null;
  return (
    <div className="lx-marquee">
      <Icon icon="mdi:bullhorn" width={13} height={13} className="lx-marquee-ic" />
      <div className="lx-marquee-track">
        <div className="lx-marquee-content">{text}</div>
      </div>
    </div>
  );
}

// ปุ่มลอยติดต่อ LINE — โชว์เฉพาะเมื่อมีลิงก์ตั้งไว้ (webSetting.lineUrl)
function ContactFab({ lineUrl }) {
  const { cfg } = useLanding();
  if (!lineUrl || cfg.fab?.visible === false) return null;
  return (
    <a className={`lx-fab${cfg.fab?.side === "left" ? " left" : ""}`} href={lineUrl} target="_blank" rel="noreferrer" title="ติดต่อแอดมิน (LINE)">
      <Icon icon="simple-icons:line" width={22} height={22} />
    </a>
  );
}

function Rail() {
  const items = useNavItems();
  return (
    <nav className="lx-rail">
      <div className="lx-card" style={{ padding: 8 }}>
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? "on" : "")}>
            <Icon icon={n.icon} width={20} className="lx-rail-ic" />
            {n.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function BottomNav() {
  const items = useNavItems();
  return (
    <nav className="lx-bottomnav">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? "on" : "")}>
          <span className="ic">
            <Icon icon={n.icon} width={21} />
          </span>
          {n.short || n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Shell() {
  const { user, isAuthenticated, isInitialized } = useAuth();
  const uid = user?.id;
  const [balance, setBalance] = useState(0);
  const [webSetting, setWebSetting] = useState(null);
  const location = useLocation();
  const dispatch = useDispatch();

  // โลโก้เว็บ (components/Logo) อ่านจาก Redux websiteSetting — ค่าเดียวกับที่ตั้งใน admin-system
  useEffect(() => {
    dispatch(getWebsiteSetting());
  }, [dispatch]);

  useEffect(() => {
    lottoApi
      .webSetting()
      .then(setWebSetting)
      .catch(() => {});
  }, []);

  const refreshBalance = useCallback(() => {
    if (!uid) return setBalance(0);
    lottoApi
      .balance(uid)
      .then(setBalance)
      .catch(() => {});
  }, [uid]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance, location.pathname]);

  useLottoSocket(uid, {
    onWallet: (p) => {
      if (p?.balance_after != null) setBalance(Number(p.balance_after));
      else refreshBalance();
      if (p?.reason === "bet_payout") pushToast("🎉 ถูกรางวัล! เงินเข้ากระเป๋าแล้ว", "win", 6000);
      if (p?.reason === "deposit" || typeof p === "string")
        pushToast("ฝากเงินสำเร็จ", "ok");
    },
    onRoundSettled: (p) => {
      pushToast(`ผลออกแล้ว — งวด #${p?.roundId ?? ""}`, "ok");
    },
  });

  return (
    <div className="lx-root vx">
      <TopBar balance={balance} onRefresh={refreshBalance} />
      <MarqueeBar text={webSetting?.marqueeText} />
      <div className="lx-layout">
        <Rail />
        <div className="lx-shell">
          <TurnBar balance={balance} path={location.pathname} />
          <Routes>
            <Route path="/" element={<HomePage balance={balance} onDone={refreshBalance} />} />
            <Route path="/play/:roundId" element={<PlayPage onDone={refreshBalance} />} />
            <Route path="/mybets" element={<MyBetsPage onChange={refreshBalance} />} />
            <Route path="/results" element={RESULTS_PAGE_ENABLED ? <ResultsPage /> : <Navigate to="/" replace />} />
            <Route path="/yeekee" element={<YeekeePage />} />
            <Route path="/yeekee/:roundId" element={<PlayPage yeekee onDone={refreshBalance} />} />
            <Route path="/account" element={<AccountPage balance={balance} onRefresh={refreshBalance} />} />
            <Route path="/deposit" element={<DepositPage onDone={refreshBalance} />} />
            <Route path="/withdraw" element={<WithdrawPage balance={balance} onDone={refreshBalance} />} />
            <Route path="/promotion" element={<PromotionPage />} />
            <Route path="/checkin" element={<CheckinPage />} />
            <Route path="/wheel" element={<WheelSpinPage />} />
            <Route path="/refer" element={<FeatureGate flag="refer" title="แนะนำเพื่อน"><ReferFriendPage onDone={refreshBalance} /></FeatureGate>} />
            <Route path="/creditcode" element={<CreditCodePage onDone={refreshBalance} />} />
            <Route path="/ranking" element={<RankingPage onDone={refreshBalance} />} />
            <Route path="/cashback" element={<CashbackPage onDone={refreshBalance} />} />
            <Route path="/activity" element={<MarketingActivityPage onDone={refreshBalance} />} />
            <Route path="/reviews" element={<FeatureGate flag="reviews" title="รีวิวจากลูกค้า"><ReviewsPage /></FeatureGate>} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="*" element={<HomePage balance={balance} />} />
          </Routes>
          {/* หน้าที่ล็อกไว้สำหรับสมาชิกมีหน้าเชิญเข้าสู่ระบบของตัวเองแล้ว (LoginGate) — แถบนี้โชว์เฉพาะหน้าสาธารณะ */}
          {!isAuthenticated && PUBLIC_CTA_PATH.test(location.pathname) && (
            <div className="vx-cta">
              <Icon icon="fluent-emoji-flat:four-leaf-clover" width={34} height={34} className="vx-cta-ic" />
              <div className="vx-cta-tx">
                <b>ยังไม่ได้เข้าสู่ระบบ</b>
                <span>เข้าสู่ระบบเพื่อแทงหวยและรับโปรโมชั่น</span>
              </div>
              <a className="lx-btn primary sm" href="/auth">
                เข้าสู่ระบบ / สมัคร
              </a>
            </div>
          )}
        </div>
      </div>
      <ContactFab lineUrl={webSetting?.lineUrl} />
      {isInitialized && location.pathname !== "/auth" && <DiamondBox uid={uid} />}
      <BottomNav />
      <Toasts />
    </div>
  );
}

export default function LottoApp() {
  return (
    <LandingProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </LandingProvider>
  );
}
